import { useEffect, useState, type RefObject } from "react";

interface TocEntry {
  id: string;
  text: string;
  level: 2 | 3;
}

/** A contents list of two entries is noise, not navigation. */
const MIN_ENTRIES = 3;

/**
 * Height of the sticky app header, in px, matching $app-header-clearance in
 * custom.scss.
 *
 * The band below is what decides which entry is highlighted as the one being
 * read, so it has to start where content actually becomes visible. This was
 * 88px against a 131px header, which meant a heading sitting hidden underneath
 * the header still counted as the section you were reading.
 */
const HEADER_HEIGHT_PX = 131;

interface DocumentTocProps {
  /** The rendered document. Headings are read from inside it. */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Heading label above the list, already translated. */
  title: string;
  /**
   * Which headings become entries. Help articles list h2 and h3; the legal
   * documents list h2 only, because a nineteen-section agreement with every
   * sub-clause included is a rail longer than the viewport.
   */
  headingSelector?: string;
  /**
   * Attribute to prefer over the heading's own text. The legal documents carry
   * `data-toc-label`, because a good section heading and a good contents entry
   * are different lengths — "1. Introduction and Controller Identity" belongs
   * above the clause, "1. Introduction" belongs in a 14rem rail.
   */
  labelAttribute?: string;
}

/**
 * Contents rail for a rendered Markdown document.
 *
 * Callers pass a `key` tied to the document on screen. React otherwise reuses
 * this component across sibling routes — /terms to /privacy is the same element
 * in the same position — and the previous document's highlighted section would
 * stay put until the observer next fired, opening a page with a section of the
 * document before it marked as the one you are reading. Remounting is the React
 * answer to that; clearing the state by hand inside the effect is a cascading
 * render.
 *
 * Entries are read back off the *rendered* headings rather than derived by
 * slugifying the Markdown a second time. rehype-slug uses github-slugger,
 * including its duplicate-heading disambiguation ("-1" suffixes), and a
 * separately written slugifier agrees on every simple heading then diverges on
 * punctuation and repeats — producing a contents list whose links look correct
 * in review and quietly go nowhere. Reading the DOM makes divergence impossible
 * by construction.
 */
export function DocumentToc({
  containerRef,
  title,
  headingSelector = "h2[id], h3[id]",
  labelAttribute,
}: DocumentTocProps) {
  const [entries, setEntries] = useState<TocEntry[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const headings = Array.from(
      container.querySelectorAll<HTMLHeadingElement>(headingSelector)
    );

    setEntries(
      headings.map((heading) => {
        const label = labelAttribute
          ? heading.getAttribute(labelAttribute)
          : null;

        // The heading contains the "#" anchor link; strip it from a clone so
        // the contents text is the heading itself.
        const clone = heading.cloneNode(true) as HTMLElement;
        clone.querySelectorAll(".help-anchor").forEach((a) => a.remove());

        return {
          id: heading.id,
          text: label ?? clone.textContent?.trim() ?? "",
          level: heading.tagName === "H3" ? 3 : 2,
        };
      })
    );

    if (headings.length < MIN_ENTRIES) return;

    // Top band of the viewport, below the sticky app header. Whichever heading
    // sits in it is the section being read.
    const observer = new IntersectionObserver(
      (records) => {
        const visible = records
          .filter((r) => r.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: `-${HEADER_HEIGHT_PX}px 0px -65% 0px`, threshold: 0 }
    );

    headings.forEach((heading) => observer.observe(heading));
    return () => observer.disconnect();
  }, [containerRef, headingSelector, labelAttribute]);

  if (entries.length < MIN_ENTRIES) return null;

  return (
    <nav className="help-toc" aria-label={title}>
      <p className="help-toc-title text-body-secondary text-uppercase mb-2">
        {title}
      </p>
      <ul className="list-unstyled mb-0">
        {entries.map((entry) => (
          <li key={entry.id}>
            <a
              href={`#${entry.id}`}
              className={[
                "help-toc-link",
                entry.level === 3 ? "help-toc-link-sub" : "",
                entry.id === activeId ? "is-active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              aria-current={entry.id === activeId ? "location" : undefined}
            >
              {entry.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
